import React, { forwardRef, PropsWithChildren, useMemo, useRef, useState } from 'react';
import { Button, Flex, HStack, Modal, ModalBody, ModalContent, ResponsiveValue, Stack, Text } from '@chakra-ui/react';
import { ChevronDownIcon } from '@/src/components/UI/FilterContainer';
import classNames from 'classnames';
import Image from 'next/image';
import { Union } from '@chakra-ui/styled-system/src/utils';
import { useResizeObserver } from '@reactuses/core';

const getTop = (el: HTMLElement): number => el.getBoundingClientRect().top + el.clientHeight + 8;
const getLeft = (el: HTMLElement): number => el.getBoundingClientRect().left;

const CriteriaButton: React.FC<{
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onReset: () => void;
  onApply: () => void;
  resultNum: number;
  buttonText: React.ReactNode;
  borderLeft?: ResponsiveValue<Union<number>>;
  borderRight?: ResponsiveValue<Union<number>>;
  contentFullSizeOnDesktop?: boolean;
}> = ({
  children,
  onClose,
  onOpen,
  isOpen,
  onReset,
  onApply,
  resultNum,
  buttonText,
  borderLeft,
  borderRight,
  contentFullSizeOnDesktop,
}) => {
  const [modalContent, setModalContent] = useState<HTMLElement | null>(null);
  const [childRef, setChildRef] = useState<HTMLElement | null>(null);
  const [heightChangeCount, setHeightChangeCount] = useState<number>(0);

  const modalTriggerRef = useRef<HTMLButtonElement>(null);

  useResizeObserver(childRef, () => {
    setHeightChangeCount((prev) => prev + 1);
  });

  const filterTop = useMemo(() => {
    if (!isOpen || !modalTriggerRef.current || !modalContent) return 0;

    let top;
    const vh = window.visualViewport.height;
    const contentHeight = modalContent.clientHeight;
    const triggerTop = getTop(modalTriggerRef.current);

    if (triggerTop + contentHeight > vh) top = Math.max(0, vh - contentHeight);
    else top = getTop(modalTriggerRef.current);

    return top;
  }, [isOpen, modalContent, children, heightChangeCount]);

  const filterLeft = useMemo(() => {
    return isOpen && modalTriggerRef.current ? getLeft(modalTriggerRef.current) : 0;
  }, [isOpen]);

  return (
    <React.Fragment>
      <Flex px={{ sm: 0 }}>
        <Flex id={'city-selector'} className={'md:flex-[none] flex-1 flex-shrink-0 overflow-hidden'}>
          <Button
            ref={modalTriggerRef}
            textColor={'black'}
            p={{ base: 4 }}
            py={{ sm: 2 }}
            w={{ sm: '20vw', base: '100%' }}
            h={'auto'}
            overflowX={'clip'}
            backgroundColor={'white'}
            borderColor={'black'}
            borderLeft={borderLeft}
            borderRight={borderRight}
            borderWidth={{ sm: isOpen ? 2 : 1, base: 1 }}
            onClick={onOpen}
          >
            <HStack w={'full'} justify={'space-between'} align={'center'}>
              <HStack className={'flex-1'} align={'center'} overflow={'hidden'}>
                {buttonText}
              </HStack>
              <Stack marginLeft={'auto'}>
                <ChevronDownIcon isActive={isOpen} />
              </Stack>
            </HStack>
          </Button>
        </Flex>
      </Flex>
      <Modal onClose={onClose} isOpen={isOpen}>
        <ModalContent
          ref={setModalContent}
          position={{ md: 'absolute', base: 'fixed' }}
          top={{ md: `${filterTop}px` }}
          left={{ md: `${filterLeft}px`, base: 0 }}
          bottom={{ md: 'auto', base: 0 }}
          m={{ base: 0 }}
          width={{ base: '100vw', md: 'auto' }}
          maxWidth={{ md: 'auto' }}
          height={{ base: '100vh', md: 'auto' }}
          maxHeight={{ sm: '100vh' }}
          alignSelf={'self-start'}
          borderRadius={0}
          background={{ base: 'rgba(28, 30, 33, 0.4)' }}
          zIndex={{ base: 99999 }}
        >
          <ModalBody
            display={'flex'}
            position={{ base: 'absolute', md: 'relative' }}
            bottom={{ base: 0 }}
            left={{ base: 0 }}
            px={{ base: 0 }}
            py={{ base: 4, md: 0 }}
            height={{ base: 'fit-content', md: 'auto' }}
            maxHeight={{ base: '80vh' }}
            width={{ md: '624px' }}
            minWidth={{ md: 'auto', base: '100vw' }}
            backgroundColor={'white'}
            className={'shadow-dropdown overflow-hidden'}
          >
            <Flex w={'full'} flexDirection={'column'} className={'flex-1 overflow-hidden'}>
              <Flex
                flexDirection={'column'}
                w={'full'}
                px={{ md: 8, base: 6 }}
                pt={{ md: 4 }}
                className={'flex-1 overflow-hidden'}
              >
                <Flex
                  as={'button'}
                  onClick={onClose}
                  className={'w-6 h-10 py-2'}
                  display={{ md: 'none' }}
                  alignItems={'center'}
                  justifyContent={'flex-start'}
                >
                  <Image src={'/close-outline.svg'} layout={'fixed'} width={24} height={24} />
                </Flex>
                {contentFullSizeOnDesktop ? (
                  <FullSizeWrapper ref={setChildRef}>{children}</FullSizeWrapper>
                ) : (
                  <NonFullSizeWrapper ref={setChildRef}>{children}</NonFullSizeWrapper>
                )}
                <Text
                  fontSize={'xs'}
                  color={'gray.500'}
                  bg={{ base: 'gray.100', md: 'transparent' }}
                  mx={-6}
                  px={6}
                  py={2}
                >
                  篩選不到合適物件？撥打 02-7752-3755，我們將提供高效專業的買代尋服務。
                </Text>
              </Flex>
              <HStack
                w={'full'}
                justify={'space-between'}
                borderTop={1}
                borderStyle={'solid'}
                borderColor={'gray.300'}
                py={{ md: 4, base: 2 }}
                px={{ md: 8, base: 6 }}
                className={'flex-shrink-0'}
              >
                <Button
                  bgColor={'white'}
                  fontSize={{ base: '16px' }}
                  textColor={'darkgray'}
                  textDecorationLine={'underline'}
                  onClick={onReset}
                  px={0}
                  m={0}
                >
                  <Text fontWeight={'500'}>重新設定</Text>
                </Button>
                <Button
                  px={{ base: 8 }}
                  py={{ base: 3 }}
                  fontSize={{ base: '16px' }}
                  textColor={'white'}
                  onClick={onApply}
                >
                  查看 {resultNum} 筆結果
                </Button>
              </HStack>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </React.Fragment>
  );
};

export default CriteriaButton;

const FullSizeWrapper = forwardRef<HTMLDivElement, PropsWithChildren<any>>(({ children }, ref) => {
  return (
    <div
      ref={ref}
      className={classNames('overflow-hidden', ['sm:-mx-8 sm:-mt-4'], 'flex flex-col flex-1 overflow-hidden')}
    >
      <div className={classNames('sm:mx-0 md:mb-4', 'md:w-full', 'sm:h-full', 'overflow-scroll')}>{children}</div>
    </div>
  );
});

const NonFullSizeWrapper = forwardRef<HTMLDivElement, PropsWithChildren<any>>(({ children }, ref) => {
  return (
    <div ref={ref} className={classNames('-mx-6 md:mb-4 w-screen md:w-full', 'flex flex-col flex-1 overflow-hidden')}>
      {children}
    </div>
  );
});
